import { useState, useCallback, UIEvent, useMemo, useEffect, useRef } from 'react';

import { useMatch, useLocation, matchPath } from 'react-router-dom';
import { useQuery } from 'react-query';

import { getUserData } from 'app/api/user/service';
import { ROUTES } from 'app/routes/constants';
import { useAuth, useCropFamiliesQuery } from 'hooks';
import { ROUTES_WITH_SIDE_MENU } from './constants';

export const usePageLayout = () => {
  const { users, setUserData, isBuyer } = useAuth();
  const { pathname } = useLocation();

  const { isLoading: isUserLoading } = useQuery(['user-data'], () => getUserData(), {
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: !users.length,
    onSuccess: (data) => {
      setUserData(data);
    },
  });

  const [isScrollable, setIsScrollable] = useState(true);
  const [isSubHeaderVisible, setIsSubHeaderVisible] = useState(true);
  const [position, setPosition] = useState(window.pageYOffset);

  const containerRef = useRef<HTMLElement>(null);
  const scrollTopRef = useRef(0);

  useEffect(() => {
    if (pathname) {
      setIsScrollable(true);
      setIsSubHeaderVisible(isBuyer);
    }
  }, [isBuyer, pathname]);

  const isSubHeader = Boolean(useMatch(ROUTES.buyer._));

  const isContentWithMenu =
    useMemo(() => ROUTES_WITH_SIDE_MENU.some((path) => matchPath(path, pathname)), [pathname]) && isBuyer;

  const { cropFamiliesData, isCropFamiliesLoading, isCropFamiliesFetching } = useCropFamiliesQuery({
    enabled: true,
  });

  const handleScroll = useCallback(
    (e: UIEvent) => {
      const moving = e.currentTarget.scrollTop;
      setIsSubHeaderVisible(position <= 0 || position > moving);
      setPosition(moving);
    },
    [position, setPosition],
  );
  const location = useLocation();
  const isLoading = isUserLoading || isCropFamiliesLoading || isCropFamiliesFetching;
  useEffect(() => {
    const element = containerRef.current;
    if (isContentWithMenu && element) {
      const top = element.scrollTop;

      if (top < 600) scrollTopRef.current = top;

      element.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [isContentWithMenu, isScrollable, location, isLoading]);

  return {
    isLoading,
    isScrollable,
    isSubHeader,
    isSubHeaderVisible: isSubHeader && isSubHeaderVisible,
    cropFamiliesData,
    isContentWithMenu,
    containerRef,
    onScroll: isSubHeader ? handleScroll : undefined,
    handleScrollState: setIsScrollable,
  };
};
